import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { v4 as uuidv4 } from 'uuid'
import { CHAT_QUERY_KEY, CHATS_QUERY_KEY } from 'api/queriesKeys'
import { useGetChatQuery } from 'api/queries/chat/chat'
import {
  ChatSendError,
  isAbortError,
  useSendChatMessageMutation,
} from 'api/mutations/chat/chat'
import { ChatContent } from 'api/mutations/chat/types'
import { Maybe } from 'types/Maybe'
import { ChatInputValues } from 'validations/ChatInputSchema'
import { toChatAttachment } from 'utils/toChatAttachment'
import { ADMIN_ROLE } from 'constants/roleGroups'
import { useCompanyId } from 'providers/CompanyProvider'
import { useUserRole } from 'providers/UserContextProvider'
import { toMessageContent } from 'utils/toMessageContent'
import { createChatDeltaBuffer } from 'utils/chatDeltaBuffer'
import { createPendingMessage, fromHistoryMessage } from 'utils/chatMessageFactories'
import { getChatSendErrorEffect } from 'utils/chatSendErrorEffect'
import {
  chatThreadReducer,
  initialThreadState,
  selectPendingMessages,
} from 'utils/chatThreadReducer'

const useChat = (chatId?: string) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  const queryClient = useQueryClient()
  const [thread, dispatch] = useReducer(chatThreadReducer, initialThreadState)
  const pendingMessages = selectPendingMessages(thread, chatId)
  const isSending = !!pendingMessages
  const abortControllerRef = useRef<Maybe<AbortController>>(null)
  const {
    data: chat,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
  } = useGetChatQuery(chatId, !isSending)
  const isChatMissing = historyError?.response?.status === 404
  const { mutateAsync: sendChatMessage } = useSendChatMessageMutation()
  const userRole = useUserRole()
  const selectedCompanyId = useCompanyId()
  const companyId = userRole === ADMIN_ROLE && selectedCompanyId !== null
    ? selectedCompanyId
    : undefined

  useEffect(() => () => abortControllerRef.current?.abort(), [])

  useEffect(() => {
    if (chatId && isChatMissing) history.replace('/chat')
  }, [chatId, history, isChatMissing])

  const pendingGenerationId = thread.generation?.id
  const pendingChatId = thread.generation?.chatId
  useEffect(() => {
    if (!pendingGenerationId || pendingChatId === chatId) return
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    dispatch({ type: 'generationEnded', generationId: pendingGenerationId })
  }, [chatId, pendingChatId, pendingGenerationId])

  const handleSend = useCallback(async (values: ChatInputValues) => {
    if (abortControllerRef.current) return

    const abortController = new AbortController()
    const isCurrentSend = () => abortControllerRef.current === abortController
    abortControllerRef.current = abortController

    let content: ChatContent[]
    try {
      content = await toMessageContent(values)
    } catch (error) {
      if (isCurrentSend()) abortControllerRef.current = null
      enqueueSnackbar(t('aiChat.errors.attachmentRead'), { variant: 'error' })
      throw error
    }

    const generationId = uuidv4()
    const assistantMessage = createPendingMessage('assistant', '')
    let savedChatId = chatId
    const deltaBuffer = createChatDeltaBuffer((text) => {
      if (isCurrentSend()) dispatch({ type: 'contentReceived', generationId, text })
    }, {
      schedule: (callback) => window.requestAnimationFrame(callback),
      cancel: (id) => window.cancelAnimationFrame(id),
    })
    dispatch({
      type: 'generationStarted',
      generationId,
      chatId,
      userMessage: createPendingMessage(
        'user',
        values.message,
        values.attachments.map(toChatAttachment),
      ),
      assistantMessage,
    })

    try {
      await sendChatMessage({
        chatId,
        content,
        companyId,
        signal: abortController.signal,
        onStarted: (startedChatId) => {
          savedChatId = startedChatId
          if (chatId || !isCurrentSend()) return
          unstable_batchedUpdates(() => {
            dispatch({ type: 'chatCreated', generationId, chatId: startedChatId })
            history.replace(`/chat/${startedChatId}`)
          })
        },
        onContent: (chunk) => {
          if (!isCurrentSend()) return
          deltaBuffer.push(chunk)
        },
      })
    } catch (error) {
      if (error instanceof ChatSendError) {
        const { redirectToChatList, messageKey } = getChatSendErrorEffect(error.code)
        if (redirectToChatList) history.replace('/chat')
        if (messageKey) enqueueSnackbar(t(messageKey), { variant: 'warning' })
      }
      if (!isAbortError(error)) throw error
    } finally {
      if (abortController.signal.aborted) deltaBuffer.discard()
      deltaBuffer.flush()
      queryClient.invalidateQueries(CHATS_QUERY_KEY)
      if (savedChatId) {
        await queryClient.invalidateQueries([CHAT_QUERY_KEY, savedChatId], {
          refetchInactive: true,
        })
      }
      if (isCurrentSend()) {
        abortControllerRef.current = null
        dispatch({ type: 'generationEnded', generationId })
      }
    }
  }, [chatId, companyId, enqueueSnackbar, history, queryClient, sendChatMessage, t])

  const savedMessages = chatId ? chat?.messages : undefined
  const historyMessages = useMemo(() => (
    (savedMessages || []).map(fromHistoryMessage)
  ), [savedMessages])
  const messages = useMemo(() => [
    ...historyMessages,
    ...(pendingMessages || []),
  ], [historyMessages, pendingMessages])

  return {
    messages,
    isSending,
    isHistoryLoading,
    isHistoryError: isHistoryError && !isChatMissing,
    anchorMessageId: pendingMessages?.[0].id,
    loadedChatId: chat && chatId,
    handleSend,
  }
}

export { useChat }
